import { useEffect, useRef, useState } from 'react';

export type DeviceRoll = 0 | 90 | 180 | 270;

const DEG = Math.PI / 180;
/** Ignore readings where the phone is close to flat: roll is ambiguous. */
const MIN_UPRIGHT_COMPONENT = 0.5;
/** The new dominant axis must beat the other by this factor to switch. */
const SWITCH_RATIO = 1.25;

const normalize = (angle: number): DeviceRoll =>
  ((((Math.round(angle / 90) * 90) % 360) + 360) % 360) as DeviceRoll;

/** Counter-clockwise roll of the phone, snapped to 90 degrees, from a
 * deviceorientation reading. Returns null when the roll is ambiguous. Uses
 * the same device frame on iOS and Android, unlike devicemotion's gravity
 * sign. `current` provides hysteresis around the 45 degree boundaries.
 */
export const rollFromOrientation = (
  beta: number,
  gamma: number,
  current: DeviceRoll,
): DeviceRoll | null => {
  const ax = -Math.sin(gamma * DEG) * Math.cos(beta * DEG);
  const ay = Math.sin(beta * DEG);
  if (Math.hypot(ax, ay) < MIN_UPRIGHT_COMPONENT) return null;
  const next = normalize(Math.atan2(ax, ay) / DEG);
  if (next === current) return null;
  const crossesAxis = (current % 180 === 0) !== (next % 180 === 0);
  const dominant = Math.max(Math.abs(ax), Math.abs(ay));
  const other = Math.min(Math.abs(ax), Math.abs(ay));
  return crossesAxis && dominant < other * SWITCH_RATIO ? null : next;
};

const screenAngle = (): number => {
  const angle = window.screen?.orientation?.angle;
  return typeof angle === 'number' ? angle : 0;
};

/** iOS only delivers orientation events after a user-gesture permission. */
export const requestDeviceOrientationAccess = async (): Promise<void> => {
  const api = (
    typeof DeviceOrientationEvent === 'undefined'
      ? undefined
      : DeviceOrientationEvent
  ) as
    | (typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<string>;
      })
    | undefined;
  try {
    await api?.requestPermission?.();
  } catch {
    // Without permission the roll stays 0 and capture behaves as before.
  }
};

/** How far (counter-clockwise, degrees) the phone is turned relative to the
 * display, which is what makes camera frames look sideways when the OS is
 * portrait-locked. Rotating the frame back by this much makes it upright.
 * Always 0 when the display itself follows the phone.
 */
export function useDeviceRoll(active: boolean) {
  const rollRef = useRef<DeviceRoll>(0);
  const physicalRef = useRef<DeviceRoll>(0);
  const [roll, setRoll] = useState<DeviceRoll>(0);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    const apply = () => {
      const next = normalize(physicalRef.current - screenAngle());
      if (next === rollRef.current) return;
      rollRef.current = next;
      setRoll(next);
    };
    const onOrientation = (event: DeviceOrientationEvent) => {
      if (event.beta == null || event.gamma == null) return;
      const next = rollFromOrientation(
        event.beta,
        event.gamma,
        physicalRef.current,
      );
      if (next === null) return;
      physicalRef.current = next;
      apply();
    };
    window.addEventListener('deviceorientation', onOrientation);
    window.screen?.orientation?.addEventListener?.('change', apply);
    return () => {
      window.removeEventListener('deviceorientation', onOrientation);
      window.screen?.orientation?.removeEventListener?.('change', apply);
      physicalRef.current = 0;
      rollRef.current = 0;
      setRoll(0);
    };
  }, [active]);

  return { rollRef, roll };
}
